// src/slices/agentManagement/reducer.ts
// Redux slice for Agent Management

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAgent, AgentManagementState } from './types';
import { fetchAgents, createAgent, updateAgent, checkAgentCode, previewNextAgentCode, changeAgentStatus } from './thunk';

const initialState: AgentManagementState = {
  agents: [],
  selectedAgent: null,
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  },
  filters: {
    searchTerm: '',
  },
  sortBy: 'code',
  sortOrder: 'asc',
  loading: false,
  error: null,
  checkingCode: false,
  codeAvailable: false,
  previewingCode: false,
  previewedCode: '',
  updatingStatus: false,
  modalState: {
    create: false,
    edit: false,
    delete: false,
  },
};

const agentManagementSlice = createSlice({
  name: 'AgentManagement', // IMPORTANT: Uppercase to match UserManagement
  initialState,
  reducers: {
    // Data actions
    setSelectedAgent: (state, action: PayloadAction<IAgent | null>) => {
      state.selectedAgent = action.payload;
    },
    
    // Modal actions
    openModal: (state, action: PayloadAction<keyof AgentManagementState['modalState']>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof AgentManagementState['modalState']>) => {
      state.modalState[action.payload] = false;
    },
    closeAllModals: (state) => {
      state.modalState = { create: false, edit: false, delete: false };
    },
    
    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1; // Reset to first page
    },
    
    // Filter actions
    setFilters: (state, action: PayloadAction<Partial<AgentManagementState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = { searchTerm: '' };
      state.pagination.currentPage = 1;
    },
    
    // Sort actions
    setSorting: (state, action: PayloadAction<{ field: string; order: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.field;
      state.sortOrder = action.payload.order;
    },
    
    // Code check actions
    resetCodeCheck: (state) => {
      state.checkingCode = false;
      state.codeAvailable = false;
    },
    
    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Agents
    builder
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
            .addCase(fetchAgents.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.agents = payload.items || payload.data || payload || [];
        state.pagination.totalItems = payload.totalItems || payload.totalCount || 0;
        state.pagination.totalPages = payload.totalPages || 0;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    
    // Create Agent - NO TOAST HERE (handled in component)
    builder
      .addCase(createAgent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAgent.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    
    // Update Agent - NO TOAST HERE (handled in component)
    builder
      .addCase(updateAgent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAgent.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    
    // Check Code
    builder
      .addCase(checkAgentCode.pending, (state) => {
        state.checkingCode = true;
      })
            .addCase(checkAgentCode.fulfilled, (state, action) => {
        state.checkingCode = false;
        const payload = action.payload as any;
        state.codeAvailable = payload.available || false;
      })
      .addCase(checkAgentCode.rejected, (state) => {
        state.checkingCode = false;
        state.codeAvailable = false;
      });

    // Preview mã đại lý tự sinh
    builder
      .addCase(previewNextAgentCode.pending, (state) => {
        state.previewingCode = true;
        state.previewedCode = '';
      })
      .addCase(previewNextAgentCode.fulfilled, (state, action) => {
        state.previewingCode = false;
        const payload = action.payload as any;
        state.previewedCode = payload.code || '';
      })
      .addCase(previewNextAgentCode.rejected, (state) => {
        state.previewingCode = false;
        state.previewedCode = '';
      });

    // Cập nhật trạng thái đại lý
    builder
      .addCase(changeAgentStatus.pending, (state) => {
        state.updatingStatus = true;
      })
      .addCase(changeAgentStatus.fulfilled, (state) => {
        state.updatingStatus = false;
      })
      .addCase(changeAgentStatus.rejected, (state) => {
        state.updatingStatus = false;
      });
  },
});

export const {
  setSelectedAgent,
  openModal,
  closeModal,
  closeAllModals,
  setPage,
  setPageSize,
  setFilters,
  clearFilters,
  setSorting,
  resetCodeCheck,
  setError,
  clearError,
} = agentManagementSlice.actions;

export default agentManagementSlice.reducer;